import { Stack } from "@mui/material";
import React, { useMemo } from "react";

import { getUniqueValuesFromJson } from "../utils/dataManipulation";
import { sortData } from "../utils/dynamicDashboard";
import { addReverseToTimelineDataItems, calculateLeft } from "../utils/position";
import { cn } from "../utils/styling";
import { TimelineData, TimelineDataItem } from "./BuildingProfile/types";
import { Tooltip } from "./Tooltip";

const Timeline = ({ data: data, maxYear, minYear }: { data: TimelineData; maxYear: number; minYear: number }) => {
  const nonNull =
    data?.filter((point) => {
      return point.year >= minYear && point.year <= maxYear;
    }) || [];

  const sortedNonNull: TimelineDataItem[] = sortData({
    data: nonNull,
    dataKey: "year",
    decsending: false,
  });

  const sortedNonNullWithReverse = addReverseToTimelineDataItems(sortedNonNull, minYear);

  const sortedNonNullYears = sortData({ data: nonNull ?? [], dataKey: "year", decsending: false });
  const sortedNonNullYearsWithMinMax = [...sortedNonNullYears, { year: minYear }, { year: maxYear }];
  const allYears: number[] = getUniqueValuesFromJson(sortedNonNullYearsWithMinMax, "year") ?? [];
  const allYearWithReverse: { reverse?: boolean; year: number }[] = addReverseToTimelineDataItems(
    allYears?.map((year) => ({ year })),
    minYear
  );

  const noOfPoints = useMemo(() => {
    let nextState: { bottom: number; top: number } = { bottom: 0, top: 0 };

    allYearWithReverse?.map((yearRow) => {
      const yearPoints = sortedNonNullWithReverse?.filter((row) => row.year === yearRow.year);
      const noOfYearPoints = yearPoints?.length;

      if (yearRow.reverse && noOfYearPoints > nextState.top) {
        nextState.top = noOfYearPoints;
      }
      if (!yearRow.reverse && noOfYearPoints > nextState.bottom) {
        nextState.bottom = noOfYearPoints;
      }
    });

    return nextState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, minYear, maxYear]);

  return (
    <div
      className="w-full px-4"
      style={{
        paddingBottom: 20 + 30 * noOfPoints.bottom,
        paddingTop: 20 + 30 * noOfPoints.top,
      }}
    >
      <div className="relative  ">
        <div className="absolute left-0 right-0 top-0 z-0 h-[2px] bg-lds-surface-strong" />
        {allYearWithReverse?.map((yearRow, yearRowIndex) => {
          const yearPoints = sortedNonNullWithReverse?.filter((row) => row.year === yearRow.year);
          return (
            <>
              <span
                className={cn(
                  "absolute top-0 -translate-x-1/2 -translate-y-1/2 bg-lds-surface-bright px-1 font-medium text-lds-foreground-strong font-ds-number-semibold-md"
                )}
                key={yearRowIndex}
                style={{
                  left: `${calculateLeft(yearRow.year, minYear, maxYear)}%`,
                }}
              >
                {yearRow.year}
              </span>
              {yearPoints.map((point, idx) => {
                const labelPosition = point.reverse ? "bottom-7" : "top-6";
                const bulletPositionStyle = yearRow.reverse
                  ? {
                      top: -40 - 35 * idx - Number(yearRowIndex % 4 > 1) * 15,
                    }
                  : {
                      top: 10 + 35 * idx + Number(yearRowIndex % 4 > 1) * 15,
                    };
                return (
                  <div
                    className="absolute top-0 z-10 -translate-x-1/2 text-center"
                    key={idx}
                    style={{
                      left: `${calculateLeft(point.year, minYear, maxYear)}%`,
                      ...bulletPositionStyle,
                    }}
                  >
                    <div className="relative  ">
                      <Stack
                        className={cn(
                          "absolute w-max max-w-[8rem] -translate-x-1/2 flex-col ",
                          yearRow.reverse && "flex-col-reverse"
                        )}
                      >
                        <Tooltip className={cn(!point.tooltip && " cursor-default ")} title={point.tooltip as string}>
                          <div className={cn(" h-4  w-4    rounded-full", point.bgColor)} style={{}} />
                        </Tooltip>
                        {point.label && (
                          <span
                            className={cn(
                              "max-w-[14rem] truncate text-lds-foreground-secondary font-ds-body-regular-xxs",
                              labelPosition
                            )}
                          >
                            {point.label}
                          </span>
                        )}
                      </Stack>
                    </div>
                  </div>
                );
              })}
            </>
          );
        })}
      </div>
    </div>
  );
};

export default Timeline;
